<script setup>
import { onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import BoardEditorModal from '@/components/BoardEditorModal.vue';
import ContextMenu from '@/components/ContextMenu.vue';
import ModalHost from '@/components/ModalHost.vue';
import SiteHeader from '@/components/SiteHeader.vue';
import SiteSidebar from '@/components/SiteSidebar.vue';
import ToastStack from '@/components/ToastStack.vue';
import { useAuthStore } from '@/stores/auth';
import { useBoardsStore } from '@/stores/boards';
import { useModalStore } from '@/stores/modals';

const auth = useAuthStore();
const boards = useBoardsStore();
const modals = useModalStore();
const route = useRoute();
const router = useRouter();

// Una sola richiesta all'avvio. L'originale rifetchava l'utente in quattro
// componenti indipendenti, con una cache localStorage da 5 minuti.
onMounted(() => auth.load());

/* `?nuova-board=1` apre l'editor di board appena arrivati. È il + della
 * colonna gemella nel feed: di là non c'è la SPA, quindi la richiesta arriva
 * come query e si consuma qui — prima si pulisce l'indirizzo, così un
 * ricaricamento non riapre il modale. */
watch(
  () => route.query['nuova-board'],
  async (chiesto) => {
    if (!chiesto) return;
    const { 'nuova-board': _, ...resto } = route.query;
    router.replace({ query: resto });
    const board = await modals.open(BoardEditorModal, { board: null });
    if (board) {
      boards.reload();
      boards.segnalaMutazione('creata', board);
      router.push({ name: 'pins-by-board', params: { boardId: board.id } });
    }
  },
  { immediate: true },
);
</script>

<template>
  <a class="skip" href="#contenuto">Vai al contenuto</a>
  <SiteHeader />
  <div class="corpo">
    <SiteSidebar />
    <main id="contenuto">
      <RouterView />
    </main>
  </div>
  <ModalHost />
  <ContextMenu />
  <ToastStack />
</template>

<style scoped>
.corpo {
  display: flex;
  align-items: flex-start;
}

main {
  flex: 1;
  /* Senza questo, un contenuto largo (la masonry) allargherebbe il flex item
   * invece di restare nella colonna che avanza accanto alla barra. */
  min-width: 0;
  padding-block: var(--s-5) var(--s-8);
}

.skip {
  position: absolute;
  top: var(--s-2);
  left: var(--s-2);
  z-index: 100;
  padding: var(--s-2) var(--s-3);
  background: var(--accent);
  color: var(--accent-contrast);
  border-radius: var(--radius);
  transform: translateY(-200%);
}

.skip:focus {
  transform: none;
}
</style>
